import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import { DEFAULT_PRODUCT_IMAGE } from "@/lib/productImage";

export default function ProductCard({ product, index = 0 }) {
  const { addItem, items } = useCart();
  const available = product.in_stock !== false && (product.stock ?? 1) > 0;

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const inCart = items.find((i) => i.product_id === product.id)?.quantity || 0;
    if (product.stock != null && inCart + 1 > product.stock) {
      toast.error(`Only ${product.stock} unit${product.stock === 1 ? "" : "s"} of ${product.name} in stock`);
      return;
    }
    addItem(product, 1);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: (index % 3) * 0.08 }}
      data-testid={`product-card-${product.id}`}
    >
      <Link to={`/product/${product.id}`} className="group block">
        <div className="relative aspect-[3/4] overflow-hidden bg-secondary border border-border">
          <img
            src={DEFAULT_PRODUCT_IMAGE}
            alt={product.name}
            loading="lazy"
            className={`spotlight-img h-full w-full object-contain bg-white p-6${available ? "" : " grayscale opacity-50"}`}
          />
          <div className="absolute top-0 left-0 p-4">
            <span className="label bg-background/80 backdrop-blur px-2 py-1 text-foreground/80">
              {product.category}
            </span>
          </div>
          {!available && (
            <div className="absolute top-0 right-0 p-4">
              <span className="label bg-background/80 backdrop-blur px-2 py-1 text-destructive" data-testid={`sold-out-badge-${product.id}`}>
                Sold Out
              </span>
            </div>
          )}
          <button
            onClick={quickAdd}
            disabled={!available}
            className={`absolute bottom-0 right-0 h-12 w-12 flex items-center justify-center transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${available ? "bg-primary text-primary-foreground translate-y-full group-hover:translate-y-0" : "bg-secondary text-muted-foreground cursor-not-allowed"}`}
            data-testid={`quick-add-${product.id}`}
            aria-label={available ? `Add ${product.name} to cart` : `${product.name} is sold out`}
          >
            <Plus size={20} strokeWidth={1.5} />
          </button>
        </div>
        <div className="mt-5 flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-2xl font-normal leading-tight group-hover:text-accent transition-colors duration-300">
              {product.name}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">{product.tagline}</p>
          </div>
          <span className="font-mono text-sm shrink-0 pt-1">${product.price.toFixed(2)}</span>
        </div>
      </Link>
    </motion.div>
  );
}
